import { ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLanguage } from '../../../../../context/LanguageContext';
import { channelCreationLevel, useRights } from '../../../../../rights';
import { primarySoft } from '../../../../../theme';
import { CHANNEL_OPTIONS } from '../channels';
import type { ChannelType } from '../CampaignDefinitionStep';

interface ChannelPickerProps {
  value: ChannelType;
  onChange: (channel: ChannelType) => void;
}

function ChannelPicker({ value, onChange }: ChannelPickerProps) {
  const theme = useTheme();
  const { t } = useLanguage();
  const { level } = useRights();
  // Droits BeOne par canal : masqué sans droit, grisé en droit intermédiaire. Un canal déjà choisi (campagne
  // existante) reste affiché pour ne pas cacher la valeur enregistrée.
  const channels = CHANNEL_OPTIONS
    .map(option => ({ ...option, access: channelCreationLevel(level, option.value) }))
    .filter(({ value: channel, access }) => access !== 'none' || channel === value);

  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(_, channel: ChannelType | null) => { if (channel) onChange(channel); }}
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
    >
      {channels.map(({ value: channel, label, Icon, access }) => {
        const disabled = access !== 'full' && channel !== value;
        const button = (
          <ToggleButton
            key={channel}
            value={channel}
            disabled={disabled}
            sx={{
              flexDirection: 'column',
              gap: 0.5,
              width: 110,
              py: 1.2,
              border: `1px solid ${theme.palette.divider} !important`,
              borderRadius: '8px !important',
              color: 'text.primary',
              '&.Mui-selected': {
                backgroundColor: primarySoft(theme, 0.08),
                color: 'primary.main',
                borderColor: `${theme.palette.primary.main} !important`,
                fontWeight: 600,
              },
              '&:hover': { backgroundColor: primarySoft(theme, 0.06) },
            }}
          >
            <Icon fontSize="small" />
            <Typography variant="caption" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
              {label}
            </Typography>
          </ToggleButton>
        );
        // Un bouton désactivé ne reçoit pas le survol : le span porte l'infobulle
        return disabled
          ? <Tooltip key={channel} title={t('common.rights.channelDisabled')} arrow><span>{button}</span></Tooltip>
          : button;
      })}
    </ToggleButtonGroup>
  );
}

export default ChannelPicker;
